import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const SEV_ORDER  = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const SEV_COLOR  = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#f59e0b', LOW: '#3b82f6' };

export default function CpaFindingsInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [rows, setRows]       = useState([]);  // { client, audit, gap }
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [sevFilter, setSevFilter]     = useState('ALL');
  const [clientFilter, setClientFilter] = useState('ALL');
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setRows([]); setError('');
    try {
      const cRes = await axiosInstance.get('/partner/context/clients', { headers: authHeaders() });
      const cs = cRes.data || [];
      setClients(cs);

      const auditFetches = await Promise.all(cs.map(async c => {
        try {
          const r = await axiosInstance.get(
            `/partner/context/clients/${c.id}/audits`, { headers: authHeaders() });
          return { client: c, audits: r.data || [] };
        } catch { return { client: c, audits: [] }; }
      }));

      const certPromises = [];
      auditFetches.forEach(({ client, audits }) => {
        audits.forEach(a => {
          if (a.completed || a.lastRunAt || a.status === 'COMPLETED') {
            certPromises.push({ client, audit: a });
          }
        });
      });
      setProgress({ done: 0, total: certPromises.length });

      const aggregated = [];
      let done = 0;
      await Promise.all(certPromises.map(async ({ client, audit }) => {
        try {
          const r = await axiosInstance.get(
            `/partner/context/clients/${client.id}/audits/${audit.id}/trust-certificate`,
            { headers: authHeaders() });
          (r.data?.gaps || []).forEach(g => aggregated.push({ client, audit, gap: g }));
        } catch { /* skip — audit may not have run */ }
        done += 1; setProgress({ done, total: certPromises.length });
      }));

      aggregated.sort((a, b) => {
        const sa = SEV_ORDER[(a.gap.severity || 'MEDIUM').toUpperCase()] ?? 4;
        const sb = SEV_ORDER[(b.gap.severity || 'MEDIUM').toUpperCase()] ?? 4;
        if (sa !== sb) return sa - sb;
        return (a.client.name || '').localeCompare(b.client.name || '');
      });
      setRows(aggregated);
    } catch (e) {
      setError(e?.response?.data?.message || t('CpaFindingsInbox.errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter(r => {
    if (sevFilter !== 'ALL' && (r.gap.severity || 'MEDIUM').toUpperCase() !== sevFilter) return false;
    if (clientFilter !== 'ALL' && String(r.client.id) !== clientFilter) return false;
    return true;
  }), [rows, sevFilter, clientFilter]);

  const counts = useMemo(() => {
    const c = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
    rows.forEach(r => {
      const s = (r.gap.severity || 'MEDIUM').toUpperCase();
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [rows]);

  const openClient = (client) => {
    localStorage.setItem('partnerContextOrgId',   String(client.id));
    localStorage.setItem('partnerContextOrgName', client.name);
    navigate('/dashboard/client-view');
  };

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1180 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{t('CpaFindingsInbox.title')}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            {t('CpaFindingsInbox.subtitle')}
          </div>
        </div>
        <button onClick={load} disabled={loading}
          style={{ padding: '8px 14px', background: '#fff', color: '#3b82f6',
            border: '1px solid #3b82f6', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`} style={{ marginRight: 6 }} /> {t('CpaFindingsInbox.refresh')}
        </button>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { k: 'TOTAL',    v: rows.length,        c: '#475569',          label: t('CpaFindingsInbox.kpiTotal') },
          { k: 'CRITICAL', v: counts.CRITICAL,    c: SEV_COLOR.CRITICAL, label: t('CpaFindingsInbox.kpiCritical') },
          { k: 'HIGH',     v: counts.HIGH,        c: SEV_COLOR.HIGH,     label: t('CpaFindingsInbox.kpiHigh') },
          { k: 'MEDIUM',   v: counts.MEDIUM,      c: SEV_COLOR.MEDIUM,   label: t('CpaFindingsInbox.kpiMedium') },
          { k: 'LOW',      v: counts.LOW,         c: SEV_COLOR.LOW,      label: t('CpaFindingsInbox.kpiLow') },
        ].map(s => (
          <div key={s.k} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '12px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
        <select value={sevFilter} onChange={e => setSevFilter(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
          <option value="ALL">{t('CpaFindingsInbox.allSeverities')}</option>
          <option value="CRITICAL">{t('CpaFindingsInbox.severityCritical')}</option>
          <option value="HIGH">{t('CpaFindingsInbox.severityHigh')}</option>
          <option value="MEDIUM">{t('CpaFindingsInbox.severityMedium')}</option>
          <option value="LOW">{t('CpaFindingsInbox.severityLow')}</option>
        </select>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
          <option value="ALL">{t('CpaFindingsInbox.allClients')}</option>
          {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
          {t('CpaFindingsInbox.showingCount', { shown: filtered.length, total: rows.length })}
        </div>
      </div>

      {error && <div style={{ padding: 12, color: '#dc2626', background: '#fee2e2',
        border: '1px solid #fca5a5', borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 28, marginBottom: 12, display: 'block' }} />
          <div style={{ fontSize: 13 }}>
            {progress.total === 1
              ? t('CpaFindingsInbox.aggregatingOne', { count: progress.total })
              : t('CpaFindingsInbox.aggregatingMany', { count: progress.total })}
            {progress.total > 0 && t('CpaFindingsInbox.aggregatingProgress', { done: progress.done, total: progress.total })}…
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af', background: '#fff',
          border: '1px dashed #e5e7eb', borderRadius: 10 }}>
          <i className="fas fa-check-circle" style={{ fontSize: 36, display: 'block',
            marginBottom: 12, color: '#10b981' }} />
          <div style={{ fontWeight: 600, color: '#374151' }}>{t('CpaFindingsInbox.emptyState')}</div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.05em' }}>{t('CpaFindingsInbox.colSev')}</th>
                <th style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.05em' }}>{t('CpaFindingsInbox.colClient')}</th>
                <th style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.05em' }}>{t('CpaFindingsInbox.colAudit')}</th>
                <th style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.05em' }}>{t('CpaFindingsInbox.colControl')}</th>
                <th style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.05em' }}>{t('CpaFindingsInbox.colTest')}</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const sev = (r.gap.severity || 'MEDIUM').toUpperCase();
                const color = SEV_COLOR[sev] || '#94a3b8';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: color + '22', color, padding: '2px 8px',
                        borderRadius: 10, fontSize: 10, fontWeight: 800 }}>{sev}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.client.name}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{r.audit.name || t('CpaFindingsInbox.auditFallback', { id: r.audit.id })}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{r.gap.control}</td>
                    <td style={{ padding: '10px 12px' }}>{r.gap.test}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <button onClick={() => openClient(r.client)}
                        style={{ padding: '4px 10px', background: '#fff', color: '#3b82f6',
                          border: '1px solid #3b82f6', borderRadius: 4, cursor: 'pointer',
                          fontSize: 11, fontWeight: 600 }}>
                        {t('CpaFindingsInbox.open')} <i className="fas fa-arrow-right" style={{ marginLeft: 4 }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
